import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Sparkles, Shield, Clock, Users, Target, CheckCircle } from 'lucide-react';
import Logo from './Logo';
import FoundingForm from './FoundingForm';

const FoundingOfferSection = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  const foundingBenefits = [
    {
      icon: Target,
      title: '30% Off First Project',
      description: 'Special founding discount for our first 5 clients',
      color: 'from-red-600 to-orange-600'
    },
    {
      icon: Shield,
      title: 'Risk-Free Guarantee',
      description: 'Full refund if you\'re not satisfied with our work',
      color: 'from-orange-600 to-amber-600'
    },
    {
      icon: Clock,
      title: 'Priority Support',
      description: '24/7 dedicated support during development',
      color: 'from-amber-600 to-yellow-600'
    },
    {
      icon: Sparkles,
      title: 'Premium Quality',
      description: 'You talk directly to the people building it, start to finish',
      color: 'from-yellow-600 to-lime-600'
    }
  ];

  return (
    <section id="founding-offer" className="py-20 relative bg-gradient-to-br from-gray-900 via-red-950/20 to-orange-950/20">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex justify-center mb-6">
            <Logo size="large" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            We're New, and We're
            <span className="block pb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400 mt-1">
              Hungry
            </span>
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
            Tuneup Tech is a new agency. We're good at what we do and have the portfolio to prove it.
            So we're offering our first 5 clients a deal that makes sense for both sides.
          </p>
        </motion.div>

        {/* Founding Offer Banner */}
        <motion.div
          className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/30 rounded-2xl p-8 mb-16 text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex items-center justify-center mb-4">
            <Target className="w-8 h-8 text-orange-400 mr-3" />
            <h3 className="text-2xl font-bold text-white">Founding Client Offer</h3>
          </div>
          <p className="text-3xl font-bold text-orange-400 mb-2">30% OFF</p>
          <p className="text-gray-200 text-lg">
            Your first project + priority support + risk-free guarantee
          </p>
          <div className="mt-6 text-sm">
            <p className="font-bold text-orange-400">Only 2 spots remaining out of 5 founding clients</p>
          </div>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {foundingBenefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                className={`glass-dark border border-red-800/30 rounded-xl p-6 hover:bg-white/10 transition-all duration-300 text-center group ${index % 3 === 0 ? 'glow-red' : index % 3 === 1 ? 'glow-orange' : 'glow-amber'}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className={`w-12 h-12 bg-gradient-to-r ${benefit.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">{benefit.title}</h4>
                <p className="text-gray-200 text-sm">{benefit.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Why This Works Section */}
        <motion.div
          className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-2xl p-8 border border-white/10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="text-2xl font-bold text-white mb-6 text-center">Why This Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <motion.div
                className="w-16 h-16 bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                whileHover={{ rotate: 8, scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                <Shield className="w-8 h-8 text-white" />
              </motion.div>
              <h4 className="text-white font-semibold mb-2">Honesty Builds Trust</h4>
              <p className="text-gray-200 text-sm">We're transparent about being new, which builds more trust than fake reviews</p>
            </div>
            <div className="text-center">
              <motion.div
                className="w-16 h-16 bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                whileHover={{ rotate: 8, scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                <Users className="w-8 h-8 text-white" />
              </motion.div>
              <h4 className="text-white font-semibold mb-2">Partnership Mindset</h4>
              <p className="text-gray-200 text-sm">You get direct access to the people building your product, not a PM relay</p>
            </div>
            <div className="text-center">
              <motion.div
                className="w-16 h-16 bg-gradient-to-r from-amber-600 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                whileHover={{ rotate: 8, scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                <Sparkles className="w-8 h-8 text-white" />
              </motion.div>
              <h4 className="text-white font-semibold mb-2">Portfolio Building</h4>
              <p className="text-gray-200 text-sm">Our first 5 projects are what we show every future client — we're not phoning it in</p>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <motion.button
            className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-2xl hover:shadow-3xl inline-flex items-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsFormOpen(true)}
          >
            Apply for Founding Client Spot
            <Target className="ml-2 w-5 h-5" />
          </motion.button>
          <p className="text-gray-400 mt-4">
            Limited to 5 businesses. First come, first served.
          </p>
        </motion.div>
      </div>

      <AnimatePresence>
        {isFormOpen && (
          <FoundingForm
            onClose={() => setIsFormOpen(false)}
            onSuccess={(result) => {
              setSubmissionResult(result);
              setIsFormOpen(false);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {submissionResult && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSubmissionResult(null)}
          >
            <motion.div
              className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-md w-full"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Application Received!</h3>
                <p className="text-gray-200 mb-6">
                  Thanks for applying for a founding client spot. We'll review your project scope and get back to you within 24 hours.
                </p>
                <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-4 mb-6">
                  <p className="text-red-400 text-sm font-medium">Request ID: {submissionResult.projectId}</p>
                  <p className="text-gray-400 text-xs mt-1">Keep this ID for your records</p>
                </div>
                <motion.button
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSubmissionResult(null)}
                >
                  Got it, thanks!
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default FoundingOfferSection;
